import { forwardRef, type KeyboardEventHandler, type ReactNode } from 'react'
import clsx from 'clsx'
import { ChevronDownIcon } from '../icons/icons'
import { useFieldDensity } from './fieldDensity'
import { isKeyboardClick } from '../hooks/useClickIntent'

interface FieldSelectorTriggerProps {
  id?: string
  open: boolean
  onOpenChange: (open: boolean) => void
  disabled?: boolean
  ariaLabel?: string
  ariaInvalid?: boolean
  ariaRequired?: boolean
  /** Ids of the text that explains the control, such as why it is locked. */
  ariaDescribedBy?: string
  onBlur?: () => void
  onKeyDown?: KeyboardEventHandler<HTMLDivElement>
  className?: string
  children: ReactNode
}

/**
 * The single source of truth for field-style selector triggers. Single- and
 * multi-select controls share this component so their dimensions, interaction
 * timing, hover treatment, open ring, and chevron cannot drift apart.
 */
export const FieldSelectorTrigger = forwardRef<HTMLDivElement, FieldSelectorTriggerProps>(
  (
    {
      id,
      open,
      onOpenChange,
      disabled = false,
      ariaLabel,
      ariaInvalid,
      ariaRequired,
      ariaDescribedBy,
      onBlur,
      onKeyDown,
      className,
      children,
    },
    ref
  ) => {
    const compact = useFieldDensity() === 'compact'
    return (
      <div
        ref={ref}
        id={id}
        role="combobox"
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-invalid={ariaInvalid || undefined}
        aria-required={ariaRequired || undefined}
        aria-describedby={ariaDescribedBy}
        aria-disabled={disabled || undefined}
        tabIndex={disabled ? -1 : 0}
        onPointerDown={(event) => {
          if (disabled) return
          if (event.pointerType === 'mouse' && event.button !== 0) return
          onOpenChange(!open)
        }}
        onClick={(event) => {
          // Keyboard and assistive-technology activation can emit click without
          // a preceding pointer event. Pointer clicks already toggle on down.
          if (!disabled && isKeyboardClick(event)) onOpenChange(!open)
        }}
        onKeyDown={(event) => {
          onKeyDown?.(event)
          if (disabled || event.defaultPrevented) return
          if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            onOpenChange(true)
          }
        }}
        onBlur={onBlur}
        className={clsx(
          'field-input-control relative flex w-full items-center justify-between rounded-md border pr-10 text-left leading-5 font-[450] outline-none transition-colors',
          compact ? 'min-h-[32px] py-[5px] pl-2.5 text-sm' : 'min-h-[38px] py-2 pl-3 text-base',
          // One background and text color per state, so CSS order never decides.
          disabled
            ? 'bg-lighter text-muted'
            : 'bg-[color:var(--input-bg)] text-[color:var(--input-text)]',
          disabled
            ? 'cursor-not-allowed border-[color:var(--input-border)] opacity-100'
            : ariaInvalid
              ? 'cursor-pointer border-danger'
              : open
                ? 'cursor-pointer border-blue-500'
                : 'cursor-pointer border-[color:var(--input-border)] hover:border-[color:var(--border-info)] hover:bg-[color:color-mix(in_srgb,var(--surface-info-subtle)_50%,transparent)] focus-visible:border-blue-500',
          className
        )}
      >
        {children}
        {!disabled ? (
          <ChevronDownIcon
            size={12}
            aria-hidden="true"
            className={clsx(
              'absolute right-3 top-1/2 -translate-y-1/2 text-faint transition-transform',
              open && 'rotate-180'
            )}
          />
        ) : null}
      </div>
    )
  }
)

FieldSelectorTrigger.displayName = 'FieldSelectorTrigger'
